<template>
  <div class="card card-custom">
    <div class="card-body container-fluid py-8 px-8 py-lg-10 px-lg-10">
      <WorkQueueChrome :active-lane="'maintenance'" :crumbs="crumbs" subtitle="Endorsement" @lane="goLane" />

      <b-alert v-if="errorAlert" variant="warning" show>
        <div class="text-dark">{{ errorAlert }}</div>
      </b-alert>
      <b-alert v-if="infoAlert" variant="info" show>
        <div class="text-dark">{{ infoAlert }}</div>
      </b-alert>

      <div v-if="work">
        <h2 class="font-weight-bolder text-dark">{{ work.policyNumber }}</h2>
        <div class="text-muted mb-6">
          {{ kindLabel }} · effective {{ work.effectiveDate }} · period
          {{ work.policyStartDate }} to {{ work.policyEndDate }}
        </div>

        <div v-if="work.kind === 'add_vehicle'" class="border rounded p-4 mb-4">
          <h4 class="font-weight-bold">Add vehicle</h4>
          <p class="text-muted font-size-sm">Look up the registration to fill the vehicle. Type the annual base, or ask Finrater for it. The screen then counts the days left in the year and applies the taxes for the garaging state. The fee stays zero unless you type one.</p>
          <div class="form-row align-items-end">
            <div class="form-group col-md-4">
              <label>Registration</label>
              <input v-model="add.registration" class="form-control text-uppercase" @keyup.enter="lookUpVehicle" @blur="normaliseRegistration" />
            </div>
            <div class="form-group col-md-3">
              <label>Garaging state</label>
              <select v-model="add.garagingState" class="form-control">
                <option value="">Select…</option>
                <option v-for="state in states" :key="state" :value="state">{{ state }}</option>
              </select>
            </div>
            <div class="form-group col-md-3">
              <button type="button" class="btn btn-light-primary font-weight-bold" :disabled="lookingUp || !canLookUp" @click.prevent="lookUpVehicle">
                {{ lookingUp ? "Looking up…" : "Look up" }}
              </button>
            </div>
          </div>
          <p v-if="lookupNote" class="text-muted font-size-sm">{{ lookupNote }}</p>
          <div class="form-row">
            <div class="form-group col-md-3"><label>VIN</label><input v-model="add.vin" class="form-control text-uppercase" /></div>
            <div class="form-group col-md-2"><label>Year</label><input v-model="add.yearOfMake" class="form-control" /></div>
            <div class="form-group col-md-3"><label>Make</label><input v-model="add.make" class="form-control" /></div>
            <div class="form-group col-md-4"><label>Model</label><input v-model="add.model" class="form-control" /></div>
          </div>
          <div class="form-row">
            <div class="form-group col-md-4">
              <label>Vehicle type</label>
              <select v-model="add.vehicleTypeRaw" class="form-control">
                <option value="">Select…</option>
                <option v-if="add.vehicleTypeRaw && vehicleTypes.indexOf(add.vehicleTypeRaw) < 0" :value="add.vehicleTypeRaw">{{ add.vehicleTypeRaw }}</option>
                <option v-for="typeName in vehicleTypes" :key="typeName" :value="typeName">{{ typeName }}</option>
              </select>
            </div>
            <div class="form-group col-md-4"><label>Market value (dollars)</label><input v-model="add.marketValueDollars" type="number" min="0" step="0.01" class="form-control" /></div>
            <div class="form-group col-md-4"><label>Fee (dollars, optional)</label><input v-model="feeDollars" type="number" min="0" step="0.01" class="form-control" /></div>
          </div>
          <div class="form-group mb-0">
            <label>Annual base</label>
            <div class="mb-2">
              <button type="button" class="btn btn-sm font-weight-bold mr-2" :class="ratingMode === 'manual' ? 'btn-primary' : 'btn-light'" @click.prevent="setRatingMode('manual')">Type it</button>
              <button type="button" class="btn btn-sm font-weight-bold" :class="ratingMode === 'automatic' ? 'btn-primary' : 'btn-light'" @click.prevent="setRatingMode('automatic')">Finrater</button>
            </div>
            <div class="form-row">
              <div class="form-group col-md-4 mb-0">
                <input v-model="add.annualBaseDollars" type="number" min="0" step="0.01" class="form-control" :readonly="ratingMode === 'automatic'" />
              </div>
            </div>
            <p v-if="ratingNote" class="text-muted font-size-sm mb-0 mt-2">{{ ratingNote }}</p>
          </div>
        </div>

        <div v-if="work.kind === 'remove_vehicle'" class="border rounded p-4 mb-4">
          <h4 class="font-weight-bold">Remove vehicle</h4>
          <div class="form-group">
            <label>Search registration / VIN</label>
            <input v-model="removeSearch" class="form-control" />
          </div>
          <div class="form-group">
            <label>Vehicle</label>
            <select v-model="remove.removedVehicleKey" class="form-control">
              <option value="">Select…</option>
              <option v-for="row in filteredFleet" :key="row.key" :value="row.key">
                {{ row.registration || row.vin }} · {{ row.garagingState }}
                <span v-if="row.writtenPremiumCents == null"> · written base missing</span>
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>Reason</label>
            <select v-model="remove.removalReason" class="form-control">
              <option value="">Select…</option>
              <option value="sold_asset">Sold</option>
              <option value="total_loss">Total loss</option>
              <option value="insured_elsewhere">Insured elsewhere</option>
            </select>
          </div>
          <div class="form-group">
            <label>Written annual base (dollars) — required if the bound vehicle has none</label>
            <input v-model="remove.enteredWrittenAnnualBaseDollars" type="number" min="0" step="0.01" class="form-control" />
          </div>
          <div class="form-group mb-0">
            <label>Fee (dollars, optional)</label>
            <input v-model="feeDollars" type="number" min="0" step="0.01" class="form-control" />
          </div>
        </div>

        <div v-if="work.kind === 'update_details'" class="border rounded p-4 mb-4">
          <h4 class="font-weight-bold">Update details</h4>
          <p class="text-muted font-size-sm">Party or administration fields only. No premium change. The confirmation reprints the schedule.</p>
          <div class="form-group">
            <label>Additional insured / party note</label>
            <input v-model="nft.additionalInsuredNote" class="form-control" />
          </div>
          <div class="form-group mb-0">
            <label>Fee (dollars, optional)</label>
            <input v-model="feeDollars" type="number" min="0" step="0.01" class="form-control" />
          </div>
        </div>

        <div v-if="work.kind === 'cancel_policy'" class="border rounded p-4 mb-4">
          <h4 class="font-weight-bold">Cancel policy</h4>
          <p class="text-muted font-size-sm">
            Bulk return premium on every active vehicle at the effective date. The $500 cancellation fee is not charged unless you type a fee. Total Loss is a per-vehicle remove reason, not a whole-policy cancel.
          </p>
          <div v-for="row in predecessorFleet" :key="row.key" class="form-row align-items-end mb-2">
            <div class="col-md-6">{{ row.registration || row.vin }} · {{ row.garagingState }}</div>
            <div class="col-md-6" v-if="row.writtenPremiumCents == null">
              <label class="font-size-sm">Written annual base (dollars)</label>
              <input v-model="cancelBases[row.key]" type="number" min="0" step="0.01" class="form-control" />
            </div>
          </div>
          <div class="form-group mb-0 mt-3">
            <label>Fee (dollars, optional)</label>
            <input v-model="feeDollars" type="number" min="0" step="0.01" class="form-control" />
          </div>
        </div>

        <div class="mb-6">
          <button class="btn btn-primary font-weight-bold text-uppercase mr-2" :disabled="busy" @click.prevent="applyThenRate">
            Apply and rate
          </button>
          <button class="btn btn-light-primary font-weight-bold text-uppercase mr-2" :disabled="busy || !rated" @click.prevent="stamp">
            Stamp endorsement
          </button>
          <button class="btn btn-light font-weight-bold text-uppercase mr-2" :disabled="busy || !work.ledgerRowId" @click.prevent="generate">
            Generate confirmation
          </button>
          <button class="btn btn-light font-weight-bold text-uppercase" :disabled="busy || !work.ledgerRowId" @click.prevent="attach">
            Attach on unposted draft
          </button>
        </div>
        <p class="text-muted font-size-sm">Stamp → Generate → Attach. Do not Post from this pair.</p>

        <div v-if="rated" class="border rounded p-4">
          <h4 class="font-weight-bold">This transaction</h4>
          <div class="d-flex justify-content-between"><span>Base</span><span>{{ money(rated.basePremiumCents) }}</span></div>
          <div class="d-flex justify-content-between"><span>FSL</span><span>{{ money(rated.fslCents) }}</span></div>
          <div class="d-flex justify-content-between"><span>GST</span><span>{{ money(rated.gstCents) }}</span></div>
          <div class="d-flex justify-content-between"><span>Stamp duty</span><span>{{ money(rated.stampDutyCents) }}</span></div>
          <div class="d-flex justify-content-between"><span>Fee</span><span>{{ money(rated.feeCents) }}</span></div>
          <div class="d-flex justify-content-between"><span>Fee GST</span><span>{{ money(rated.feeGstCents) }}</span></div>
          <div class="d-flex justify-content-between font-weight-bold mt-2"><span>Invoice</span><span>{{ money(rated.invoiceTotalCents) }}</span></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WorkQueueChrome from "./components/WorkQueueChrome.vue";
import {
  getEndorsementWork,
  lookupEndorsementVehicle,
  applyEndorsementWork,
  rateEndorsementWork,
  stampEndorsementSuccessor
} from "@/services/work-queue-service";
import { generateScheduleDocument, attachSchedule, createDraft } from "@/services/insight-post-service";
import { ADD_BODY_CLASSNAME, REMOVE_BODY_CLASSNAME } from "@/core/services/store/htmlclass.module";

export default {
  name: "EndorsementPage",
  components: { WorkQueueChrome },
  data() {
    return {
      busy: false,
      lookingUp: false,
      errorAlert: null,
      infoAlert: null,
      lookupNote: "",
      ratingNote: "",
      ratingMode: "manual",
      work: null,
      add: {
        registration: "",
        vin: "",
        yearOfMake: "",
        make: "",
        model: "",
        garagingState: "",
        vehicleTypeRaw: "",
        annualBaseDollars: "",
        marketValueDollars: ""
      },
      remove: { removedVehicleKey: "", removalReason: "", enteredWrittenAnnualBaseDollars: "" },
      removeSearch: "",
      nft: { additionalInsuredNote: "" },
      cancelBases: {},
      feeDollars: "",
      states: ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"],
      vehicleTypes: [
        "Rigid/articulated - 2-5t",
        "Ute",
        "4WD",
        "Van",
        "SUV",
        "People mover",
        "Electric",
        "Trailer",
        "Passenger",
        "Passenger prestige",
        "Passenger exotic"
      ]
    };
  },
  computed: {
    crumbs() {
      return [{ label: (this.work && this.work.policyNumber) || "Endorsement" }];
    },
    kindLabel() {
      const labels = {
        add_vehicle: "Add vehicle",
        remove_vehicle: "Remove vehicle",
        update_details: "Update details",
        cancel_policy: "Cancel policy"
      };
      return (this.work && labels[this.work.kind]) || "Endorsement";
    },
    predecessorFleet() {
      return ((this.work && this.work.predecessorVehicles) || []).map((row) => ({
        ...row,
        key: String(row.zohoVehicleId || row.registration || row.vin || "").toUpperCase()
      }));
    },
    filteredFleet() {
      const q = String(this.removeSearch || "").trim().toUpperCase();
      return this.predecessorFleet.filter((row) => {
        if (!q) return true;
        return String(row.registration || "").toUpperCase().indexOf(q) >= 0
          || String(row.vin || "").toUpperCase().indexOf(q) >= 0;
      });
    },
    rated() {
      return this.work && this.work.rated;
    },
    canLookUp() {
      return String(this.add.registration || "").trim().length > 0 && !!this.add.garagingState;
    }
  },
  mounted() {
    this.$store.commit("setWizardTitle", "Underwriting Work Queue");
    this.$store.commit("setWizardSubtitle", "Endorsement");
    this.load();
  },
  methods: {
    goLane(lane) {
      this.$router.push({ path: "/workbench", query: { lane } }).catch(() => {});
    },
    async load() {
      this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
      try {
        const result = await getEndorsementWork(this.$route.params.transactionId);
        if (result && result.error) {
          this.errorAlert = result.error;
          return;
        }
        this.work = result.work || result;
      } catch (error) {
        this.errorAlert = this.apiMessage(error, "Unable to load this endorsement work item.");
      } finally {
        this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");
      }
    },
    apiMessage(error, fallback) {
      const data = error && error.response && error.response.data;
      if (data && typeof data.result === "string" && data.result) return data.result;
      if (data && data.result && data.result.error) return data.result.error;
      if (data && typeof data.error === "string") return data.error;
      return fallback;
    },
    money(cents) {
      const amount = Number(cents);
      if (!Number.isFinite(amount)) return "";
      return (amount / 100).toLocaleString("en-AU", { style: "currency", currency: "AUD" });
    },
    normaliseRegistration() {
      this.add.registration = String(this.add.registration || "").trim().toUpperCase();
    },
    setRatingMode(mode) {
      this.ratingMode = mode === "automatic" ? "automatic" : "manual";
      this.ratingNote = "";
      if (this.ratingMode === "manual") return;
      if (!this.canLookUp) {
        this.ratingNote = "Enter the registration and the garaging state, then look the vehicle up.";
        return;
      }
      this.lookUpVehicle();
    },
    applyLookup(result) {
      const form = (result && result.form) || {};
      const typedMarket = String(this.add.marketValueDollars || "").trim();
      this.add.registration = String(form.registration || this.add.registration || "").toUpperCase();
      if (form.vin) this.add.vin = String(form.vin).toUpperCase();
      if (form.yearOfMake) this.add.yearOfMake = String(form.yearOfMake);
      if (form.make) this.add.make = form.make;
      if (form.model) this.add.model = form.model;
      if (form.garagingState) this.add.garagingState = form.garagingState;
      if (form.vehicleTypeRaw) this.add.vehicleTypeRaw = form.vehicleTypeRaw;
      if (!typedMarket && form.marketValueDollars) this.add.marketValueDollars = form.marketValueDollars;
      const name = [form.yearOfMake, form.make, form.model].filter(Boolean).join(" ");
      const via = form.source ? ` Source: ${form.source}.` : "";
      const market = !typedMarket && form.marketValueDollars
        ? " Check the market value against the endorsement sheet."
        : "";
      this.lookupNote = `${name || "Vehicle found"}.${via}${market}`;
      if (result.ratingMode === "automatic" && result.annualBaseDollars) {
        this.ratingMode = "automatic";
        this.add.annualBaseDollars = result.annualBaseDollars;
        this.ratingNote = "Finrater returned this annual base. The screen still counts the days and the taxes.";
        return;
      }
      if (this.ratingMode === "automatic") {
        this.ratingMode = "manual";
        this.ratingNote = result.ratingError || "Finrater did not return an annual base. Type it.";
      }
    },
    async lookUpVehicle() {
      this.normaliseRegistration();
      if (!this.canLookUp || this.lookingUp) return;
      this.lookingUp = true;
      this.errorAlert = null;
      this.lookupNote = "";
      if (this.ratingMode === "automatic") this.ratingNote = "Asking Finrater…";
      try {
        const result = await lookupEndorsementVehicle({
          rego: this.add.registration,
          state: this.add.garagingState,
          ratingMode: this.ratingMode
        });
        if (result && result.error) {
          this.errorAlert = result.error;
          if (this.ratingMode === "automatic") {
            this.ratingMode = "manual";
            this.ratingNote = "Type the annual base.";
          }
          return;
        }
        this.applyLookup(result);
      } catch (error) {
        this.errorAlert = this.apiMessage(error, "Unable to look up that registration.");
        if (this.ratingMode === "automatic") {
          this.ratingMode = "manual";
          this.ratingNote = "Finrater was not asked. Type the annual base.";
        }
      } finally {
        this.lookingUp = false;
      }
    },
    applyBody() {
      const feeDollars = this.feeDollars === "" ? undefined : this.feeDollars;
      if (this.work.kind === "add_vehicle") {
        return {
          addedVehicle: {
            ...this.add,
            coverType: "Comprehensive",
            coverDisplay: "Section 1 & 2",
            settlementBasis: "market_value",
            situationSource: "vehicle",
            vehicleCategoryCode: String(this.add.vehicleTypeRaw || "UTE")
              .toUpperCase()
              .replace(/[^A-Z0-9]+/g, "_")
              .replace(/^_+|_+$/g, "")
              .slice(0, 64) || "UTE"
          },
          feeDollars
        };
      }
      if (this.work.kind === "remove_vehicle") {
        return { ...this.remove, feeDollars };
      }
      if (this.work.kind === "update_details") {
        return {
          partyEdits: { additionalInsureds: this.nft.additionalInsuredNote ? [this.nft.additionalInsuredNote] : [] },
          feeDollars
        };
      }
      return { enteredWrittenAnnualBaseByKey: this.cancelBases, feeDollars };
    },
    async applyThenRate() {
      this.busy = true;
      this.errorAlert = null;
      try {
        const applied = await applyEndorsementWork(this.$route.params.transactionId, this.applyBody());
        if (applied && applied.error) {
          this.errorAlert = applied.error;
          return;
        }
        const rated = await rateEndorsementWork(this.$route.params.transactionId);
        if (rated && rated.error) {
          this.errorAlert = rated.error;
          return;
        }
        this.work = (rated && rated.work) || this.work;
      } catch (error) {
        this.errorAlert = this.apiMessage(error, "Unable to apply or rate this endorsement.");
      } finally {
        this.busy = false;
      }
    },
    async stamp() {
      this.busy = true;
      this.errorAlert = null;
      try {
        const result = await stampEndorsementSuccessor(this.$route.params.transactionId);
        if (result && result.error) {
          this.errorAlert = result.error;
          return;
        }
        this.infoAlert = "Endorsement stamped. Generate the confirmation next.";
      } catch (error) {
        this.errorAlert = this.apiMessage(error, "Unable to stamp the endorsement.");
      } finally {
        this.busy = false;
      }
    },
    async generate() {
      this.busy = true;
      this.errorAlert = null;
      try {
        const result = await generateScheduleDocument(this.work.ledgerRowId);
        if (result && result.error) {
          this.errorAlert = result.error;
          return;
        }
        this.infoAlert = "Confirmation generated. Attach it on the unposted Insight draft. Do not Post.";
      } catch (_e) {
        this.errorAlert = "Unable to generate the confirmation.";
      } finally {
        this.busy = false;
      }
    },
    async attach() {
      this.busy = true;
      this.errorAlert = null;
      try {
        const draft = await createDraft(this.work.ledgerRowId);
        if (draft && draft.error) {
          this.errorAlert = draft.error;
          return;
        }
        const attached = await attachSchedule(this.work.ledgerRowId);
        if (attached && attached.error) {
          this.errorAlert = attached.error;
          return;
        }
        this.infoAlert = "Attached on the unposted Insight draft. Stop here. A human posts later.";
      } catch (_e) {
        this.errorAlert = "Unable to attach the confirmation.";
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>
